import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactPaginate from "react-paginate";
import "./index.scss";
import {
  useDeleteOfferMutation,
  useGetOffersQuery,
} from "store/offer/offerApi";
import { useSelector } from "react-redux";
import Loader from "@components/Loader/Loader";
import CustomTable from "@components/CustomTable/CustomTable";
import CustomActionMenu from "@components/CustomActionMenu/CustomActionMenu";
import CreateOfferComponent from "@components/CreateOfferComponent/CreateOfferComponent";
import { CreateModalContext } from "layouts/appLayout/layout";
import CustomDialog from "@components/CustomDialog/CustomDialog";
import { toast } from "react-toastify";
import CustomHeader from "@components/CustomHeader/CustomHeader";

const OffersPage = () => {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.auth.user);
  const companyId = currentUser?.companyId?._id;

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState(null);
  const [searchText, setSearchText] = useState("");

  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10; // Nombre d'offres par page

  const { toggleCreateModal } = useContext(CreateModalContext);

  const { data: offers, isLoading: isLoadingOffers } = useGetOffersQuery(
    companyId,
    { skip: !companyId }
  );

  const [deleteOffer, { isLoading: isDeletingLoading }] =
    useDeleteOfferMutation();

  // ✅ Permissions
  const canEditOffer =
    currentUser?.roleId?.permissions.includes("UPDATE_OFFER");
  const canDeleteOffer =
    currentUser?.roleId?.permissions.includes("DELETE_OFFER");

  const headers = [
    { key: "nom", label: t("offer.create_form.name") },
    { key: "price", label: t("offer.create_form.price") },
    { key: "type", label: t("offer.create_form.type") },
    { key: "description", label: t("offer.create_form.description") },
    { key: "classeComptable", label: t("offer.create_form.class") },
    { key: "categorie", label: t("offer.create_form.category") },
  ];

  const renderCell = (key, offer) => {
    const value = offer[key];
    return value === null || value === undefined || value === "" ? "-" : value;
  };

  const handleEditOffer = (offer) => {
    if (!canEditOffer) return toast.error(t("offer.no_permission"));
    toggleCreateModal(
      <CreateOfferComponent
        onClose={() => toggleCreateModal(<></>)}
        offer={offer}
      />,
      t("offer.update_form_name")
    );
  };

  const handleDeleteOffer = (offer) => {
    if (!canDeleteOffer) return toast.error(t("offer.no_permission"));
    setOfferToDelete(offer);
    setIsDeleteDialogOpen(true);
  };

  const showActionColumn = canEditOffer || canDeleteOffer;

  const actionMenu = showActionColumn
    ? (offer) => (
        <CustomActionMenu
          data={offer}
          actions={[
            ...(canEditOffer
              ? [
                  {
                    label: t("offer.actions.edit"),
                    onClick: () => handleEditOffer(offer),
                  },
                ]
              : []),
            ...(canDeleteOffer
              ? [
                  {
                    label: t("offer.actions.delete"),
                    onClick: () => handleDeleteOffer(offer),
                  },
                ]
              : []),
          ]}
        />
      )
    : undefined;

  const onSearchOffer = (text) => setSearchText(text.toLowerCase());

  const filteredOffers = useMemo(() => {
    if (!offers?.data) return [];
    if (searchText.length <= 1) return offers.data;

    return offers.data.filter((offer) => {
      const name = offer.nom?.toLowerCase() || "";
      const description = offer.description?.toLowerCase() || "";
      return name.includes(searchText) || description.includes(searchText);
    });
  }, [offers?.data, searchText]);

  // ✅ Pagination
  const pageCount = Math.ceil(filteredOffers.length / itemsPerPage);
  const currentItems = filteredOffers.slice(
    currentPage * itemsPerPage,
    currentPage * itemsPerPage + itemsPerPage
  );

  const handlePageClick = ({ selected }) => setCurrentPage(selected);

  if (isLoadingOffers) return <Loader />;

  return (
    <div className="p-5" style={{ overflowX: "auto" }}>
      <CustomHeader
        title={t("offer.header_title")}
        dataLength={filteredOffers?.length}
        onSearch={onSearchOffer}
      />
      <CustomTable
        headers={headers}
        data={currentItems}
        rowKey="_id"
        renderCell={renderCell}
        actionMenu={actionMenu}
      />

      {/* ✅ Pagination */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
        <ReactPaginate
          previousLabel={"←"}
          nextLabel={"→"}
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={1}
          pageRangeDisplayed={2}
          onPageChange={handlePageClick}
          containerClassName={"pagination"}
          activeClassName={"active"}
          forcePage={currentPage}
        />
      </div>

      {isDeleteDialogOpen && (
        <CustomDialog
          open={isDeleteDialogOpen}
          setOpen={setIsDeleteDialogOpen}
          title={t("offer.delete_dialog.title")}
          description={t("offer.delete_dialog.description")}
          cancelText={t("offer.delete_dialog.cancel")}
          submitText={t("offer.delete_dialog.submit")}
          onSubmit={async () => {
            try {
              await deleteOffer(offerToDelete._id).unwrap();
              setIsDeleteDialogOpen(false);
              toast.success(t("offer.create_form.delete_success"));
            } catch (error) {
              toast.error(t("offer.create_form.delete_error"));
            }
          }}
          isLoading={isDeletingLoading}
        />
      )}
    </div>
  );
};

export default OffersPage;
